import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Support as SupportIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { WalteroCard, WalteroTypography, WalteroButton } from './waltero';
import organizationRoleService from '../services/organizationRoleService';
import { hasAdminAccess } from '../utils/auth';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

const OrganizationRoleManagement = ({ organization, open, onClose, onRoleChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roleAssignments, setRoleAssignments] = useState({
    accountManagers: [],
    supportResponsible: []
  });
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [selectedRoleType, setSelectedRoleType] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [searchingUsers, setSearchingUsers] = useState(false);

  useEffect(() => {
    if (open && organization) {
      loadRoleAssignments();
    }
  }, [open, organization]);

  // Debounce user search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (userSearchTerm) {
        searchUsers(userSearchTerm);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [userSearchTerm]);

  // Check admin access after hooks
  if (!hasAdminAccess()) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Admin access required to manage organization roles.
      </Alert>
    );
  }

  const loadRoleAssignments = async () => {
    if (!organization?.id) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await organizationRoleService.getOrganizationRoles(organization.id);
      setRoleAssignments(response.data);
    } catch (err) {
      console.error('Error loading role assignments:', err);
      setError('Failed to load role assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = (roleType) => {
    setSelectedRoleType(roleType);
    setAddUserOpen(true);
    setUserSearchTerm('');
    setSelectedUser(null);
    setAvailableUsers([]);
  };

  const handleRemoveUser = async (userId, roleType) => {
    if (!organization?.id) return;

    try {
      setAssigning(true);
      await organizationRoleService.removeUserFromRole(organization.id, userId, roleType);
      
      // Refresh role assignments
      await loadRoleAssignments();
      
      // Notify parent component
      if (onRoleChange) {
        onRoleChange();
      }
    } catch (err) {
      console.error('Error removing user from role:', err);
      setError('Failed to remove user from role');
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignUser = async () => {
    if (!selectedUser || !selectedRoleType || !organization?.id) return;

    try {
      setAssigning(true);
      await organizationRoleService.assignUserToRole(
        organization.id, 
        selectedUser.id, 
        selectedRoleType
      );
      
      // Close dialog and refresh
      setAddUserOpen(false);
      await loadRoleAssignments();
      
      // Notify parent component
      if (onRoleChange) {
        onRoleChange();
      }
    } catch (err) {
      console.error('Error assigning user to role:', err);
      setError('Failed to assign user to role');
    } finally {
      setAssigning(false);
    }
  };

  const searchUsers = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setAvailableUsers([]);
      setSearchingUsers(false);
      return;
    }

    try {
      setSearchingUsers(true);
      const response = await axios.get(`${API_BASE_URL}/users?search=${encodeURIComponent(searchTerm)}&limit=20`);
      setAvailableUsers(response.data.data || []);
    } catch (err) {
      console.error('Error searching users:', err);
      setAvailableUsers([]);
    } finally {
      setSearchingUsers(false);
    }
  };

  const renderRoleSection = (roleType, assignments, icon, title, color) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          {icon}
          <Typography variant="h6" sx={{ ml: 1 }}>
            {title} ({assignments.length})
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleAddUser(roleType)}
            disabled={assigning}
          >
            Add User
          </Button>
        </Box>

        {assignments.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No {title.toLowerCase()} assigned
          </Typography>
        ) : (
          <List dense>
            {assignments.map((assignment, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <Avatar sx={{ mr: 2, bgcolor: color }}>
                  <PersonIcon />
                </Avatar>
                <ListItemText
                  primary={
                    assignment.user?.firstname && assignment.user?.lastname
                      ? `${assignment.user.firstname} ${assignment.user.lastname}`
                      : assignment.user?.email || 'Unknown User'
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        {assignment.user?.email}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="textSecondary">
                        Assigned: {new Date(assignment.assignedDate).toLocaleDateString()}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title={`Remove from ${title.toLowerCase()}`}>
                    <IconButton
                      edge="end"
                      onClick={() => handleRemoveUser(assignment.userId, roleType)}
                      disabled={assigning}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        Role Management for {organization?.name}
      </Typography>

      {renderRoleSection(
        'account-manager',
        roleAssignments.accountManagers || [],
        <BusinessIcon color="primary" />,
        'Account Managers',
        'primary.main'
      )}

      {renderRoleSection(
        'support-responsible',
        roleAssignments.supportResponsible || [],
        <SupportIcon color="secondary" />,
        'Support Responsible',
        'secondary.main'
      )}

      {/* Add User Dialog */}
      <Dialog open={addUserOpen} onClose={() => setAddUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Add User as {selectedRoleType === 'account-manager' ? 'Account Manager' : 'Support Responsible'}
            </Typography>
            <IconButton onClick={() => setAddUserOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Search for a user to assign to this role:
            </Typography>
            <Autocomplete
              options={availableUsers}
              getOptionLabel={(option) => 
                option.firstname && option.lastname 
                  ? `${option.firstname} ${option.lastname} (${option.email})`
                  : option.email || 'Unknown User'
              }
              value={selectedUser}
              onChange={(event, newValue) => setSelectedUser(newValue)}
              onInputChange={(event, newInputValue) => {
                setUserSearchTerm(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search users"
                  placeholder="Type to search for users..."
                  variant="outlined"
                  fullWidth
                />
              )}
              loading={searchingUsers}
              noOptionsText={userSearchTerm.length < 2 ? "Type at least 2 characters to search" : "No users found"}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignUser}
            variant="contained"
            disabled={!selectedUser || assigning}
          >
            {assigning ? <CircularProgress size={20} /> : 'Assign User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationRoleManagement;
